import type { LinhaDaTrilha, PalavraDaTrilha } from '../lib/api';
import { Icone } from './Icone';

/**
 * ⭐⭐ **A TRILHA DA APURAÇÃO no cartão da parte — decisão 13, 13/09/2026.**
 *
 * ⭐ **Redesenhada em 15/09/2026, a pedido dele:** a primeira versão era uma
 * lista de catorze linhas com a palavra à direita — *"dá para fazer uma trilha
 * mais profissional e tecnológica, sofisticada, didática"*. Ficou assim:
 *
 * 1. **O percurso** — cinco etapas, no desenho da escada do caso, cada uma com
 *    quantos levantamentos já têm resposta e o estado da etapa por extenso.
 * 2. **As etapas abertas** — um painel por etapa, cada levantamento com o seu
 *    sinal e a palavra. A linha leva ao tópico onde está o botão.
 * 3. **"Como ler a trilha"** — a legenda dos nove sinais, recolhida.
 *
 * ⚠️ **As oito palavras são as dele e não mudaram.** O que entrou foi o sinal
 * ao lado de cada uma, e a cor só separa três famílias: púrpura para o que tem
 * resposta, ouro para o que pede atenção, e o apagado para o que está fora do
 * alcance. ⛔ **Não há percentual**: a barra saiu do Hub em 31/08 porque o
 * denominador era errado. A conta da etapa é "tantos de tantos", e o
 * denominador exclui o que o nível não contrata e o que o Hub não alcança.
 *
 * ⚠️ **As palavras vêm do servidor** (`lib/trilha-da-apuracao.ts`); aqui só se
 * agrupa e se desenha. A trilha não tem botão próprio e nunca faz consulta.
 */
const ETAPAS: Array<{ nome: string; chaves: string[] }> = [
  { nome: 'Identificação', chaves: ['qualificacao', 'vinculos'] },
  { nome: 'Processos e dívidas', chaves: ['processos', 'passivo'] },
  { nome: 'Bens móveis', chaves: ['veiculos', 'aeronaves', 'embarcacoes'] },
  { nome: 'Imóveis', chaves: ['imoveis_uniao', 'imoveis_por_titular', 'indisponibilidade'] },
  { nome: 'Atos e registros', chaves: ['atos', 'junta', 'marcas', 'diarios_oficiais'] },
];

type Sinal = PalavraDaTrilha | 'em_branco';

const RESPONDIDO: Sinal[] = ['feito', 'nada_consta'];
const ATENCAO: Sinal[] = ['falta', 'nao_respondeu', 'a_confirmar'];
const FORA: Sinal[] = ['fora_do_nivel', 'sem_caminho'];

const sinalDe = (l: LinhaDaTrilha): Sinal => l.palavra ?? 'em_branco';

export function dizer(l: LinhaDaTrilha): string {
  switch (l.palavra) {
    case 'feito':
      return 'Feito';
    case 'nada_consta':
      return 'Nada consta';
    case 'falta':
      return `Falta ${l.detalhe ?? 'dado'}`;
    case 'nao_respondeu':
      return 'Não respondeu';
    case 'aguardando':
      return `Aguardando ${l.detalhe ?? ''}`.trim();
    case 'a_confirmar':
      return l.detalhe ? `A confirmar · ${l.detalhe}` : 'A confirmar';
    case 'fora_do_nivel':
      return 'Fora deste nível';
    case 'sem_caminho':
      return 'Sem caminho no Hub';
    default:
      return 'Em branco';
  }
}

/** O sinal de cada estado — desenhado aqui, em traço e `currentColor`. */
function Marca({ sinal }: { sinal: Sinal }) {
  const miolo = (() => {
    switch (sinal) {
      case 'feito':
        return <path d="M5 8.2l2 2 4-4.4" />;
      case 'nada_consta':
        return <path d="M5.2 8h5.6" />;
      case 'falta':
        return (
          <>
            <path d="M8 4.8v3.6" />
            <path d="M8 10.9v.1" />
          </>
        );
      case 'nao_respondeu':
        return <path d="M5.8 5.8l4.4 4.4M10.2 5.8l-4.4 4.4" />;
      case 'a_confirmar':
        return (
          <>
            <path d="M6.4 6.3a1.7 1.7 0 1 1 2.4 1.6c-.6.3-.8.7-.8 1.2v.2" />
            <path d="M8 11.1v.1" />
          </>
        );
      case 'aguardando':
        return <path d="M8 5.2V8l2 1.3" />;
      case 'fora_do_nivel':
        return <path d="M4.2 11.8l7.6-7.6" />;
      case 'sem_caminho':
        return <path d="M4.8 8h1.4M7.3 8h1.4M9.8 8h1.4" />;
      default:
        return null;
    }
  })();
  return (
    <span className={`trilha-marca marca-${sinal}`} aria-hidden="true">
      <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="8" cy="8" r="6.6" className="trilha-marca-aro" />
        {miolo}
      </svg>
    </span>
  );
}

/** O estado da etapa, por extenso — o que se lê primeiro no percurso. */
function estadoDaEtapa(linhas: LinhaDaTrilha[]): { rotulo: string; classe: string; respondidos: number; alcance: number } {
  const sinais = linhas.map(sinalDe);
  const alcance = sinais.filter((s) => !FORA.includes(s)).length;
  const respondidos = sinais.filter((s) => RESPONDIDO.includes(s)).length;
  if (sinais.some((s) => ATENCAO.includes(s))) return { rotulo: 'Pede atenção', classe: 'atencao', respondidos, alcance };
  if (sinais.includes('aguardando')) return { rotulo: 'Aguardando', classe: 'aguardando', respondidos, alcance };
  if (alcance === 0) return { rotulo: 'Fora do alcance', classe: 'fora', respondidos, alcance };
  if (respondidos === alcance) return { rotulo: 'Concluída', classe: 'concluida', respondidos, alcance };
  if (respondidos > 0) return { rotulo: 'Em andamento', classe: 'andamento', respondidos, alcance };
  return { rotulo: 'Não iniciada', classe: 'nao-iniciada', respondidos, alcance };
}

const LEGENDA: Array<{ sinal: Sinal; palavra: string; quer: string }> = [
  { sinal: 'feito', palavra: 'Feito', quer: 'o levantamento foi feito e trouxe resultado.' },
  { sinal: 'nada_consta', palavra: 'Nada consta', quer: 'foi feito e nada foi encontrado — é resultado do trabalho.' },
  { sinal: 'em_branco', palavra: 'Em branco', quer: 'ainda não foi feito, e nada impede fazer.' },
  { sinal: 'falta', palavra: 'Falta [dado]', quer: 'a pesquisa não sai sem esse dado na ficha da parte.' },
  { sinal: 'nao_respondeu', palavra: 'Não respondeu', quer: 'a fonte falhou, e a pesquisa ainda não foi refeita.' },
  { sinal: 'a_confirmar', palavra: 'A confirmar', quer: 'há achado que depende de confirmação.' },
  { sinal: 'aguardando', palavra: 'Aguardando', quer: 'há pedido feito, esperando resposta.' },
  { sinal: 'fora_do_nivel', palavra: 'Fora deste nível', quer: 'não faz parte do que o nível do caso contrata.' },
  { sinal: 'sem_caminho', palavra: 'Sem caminho no Hub', quer: 'faz-se fora do Hub e lança-se à mão.' },
];

export function TrilhaDaApuracao({
  linhas,
  topicoAtual,
  aoIrAoTopico,
}: {
  linhas: LinhaDaTrilha[];
  /** O tópico em que a trilha está — a linha dele não vira link. */
  topicoAtual: string;
  aoIrAoTopico?: (chave: string) => void;
}) {
  const conhecidas = new Set(ETAPAS.flatMap((e) => e.chaves));
  const etapas = [
    ...ETAPAS.map((e) => ({
      nome: e.nome,
      linhas: e.chaves.map((c) => linhas.find((l) => l.chave === c)).filter((l): l is LinhaDaTrilha => !!l),
    })),
    { nome: 'Outros', linhas: linhas.filter((l) => !conhecidas.has(l.chave)) },
  ].filter((e) => e.linhas.length > 0);

  const sinais = linhas.map(sinalDe);
  const conta = (lista: Sinal[]) => sinais.filter((s) => lista.includes(s)).length;
  const resumo = [
    [conta(RESPONDIDO), 'com resposta'],
    [conta(ATENCAO), 'pedem atenção'],
    [conta(['aguardando']), 'aguardando'],
    [conta(['em_branco']), 'em branco'],
  ].filter(([n]) => (n as number) > 0) as Array<[number, string]>;

  return (
    <div className="trilha-apuracao">
      <ol className="trilha-percurso" aria-label="Etapas da apuração">
        {etapas.map((e, i) => {
          const est = estadoDaEtapa(e.linhas);
          return (
            <li key={e.nome} className={`etapa-${est.classe}`}>
              <span className="trilha-ponto" aria-hidden="true">
                {est.classe === 'concluida' ? (
                  <Icone nome="check" tamanho={11} />
                ) : (
                  <span className="trilha-ponto-numero">{i + 1}</span>
                )}
              </span>
              <span className="trilha-etapa-nome">{e.nome}</span>
              <span className="trilha-etapa-estado">
                {est.rotulo}
                {est.alcance > 0 && (
                  <span className="dado">
                    {' '}
                    · {est.respondidos}/{est.alcance}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>

      {resumo.length > 0 && (
        <p className="trilha-resumo">
          {resumo.map(([n, oQue], i) => (
            <span key={oQue}>
              {i > 0 && <span className="trilha-resumo-sep" aria-hidden="true">·</span>}
              <strong className="dado">{n}</strong> {oQue}
            </span>
          ))}
        </p>
      )}

      <div className="trilha-etapas">
        {etapas.map((e) => {
          const est = estadoDaEtapa(e.linhas);
          return (
            <section key={e.nome} className="trilha-etapa" aria-label={e.nome}>
              <header className="trilha-etapa-topo">
                <span className="trilha-etapa-titulo">{e.nome}</span>
                {est.alcance > 0 && (
                  <span className="trilha-etapa-conta dado" title={`${est.respondidos} de ${est.alcance} com resposta`}>
                    {est.respondidos}/{est.alcance}
                  </span>
                )}
              </header>
              <ul>
                {e.linhas.map((l) => {
                  const sinal = sinalDe(l);
                  const corpo = (
                    <>
                      <Marca sinal={sinal} />
                      <span className="trilha-item-texto">
                        <span className="trilha-item-nome">{l.nome}</span>
                        <span className={`trilha-item-palavra palavra-${sinal}`}>{dizer(l)}</span>
                      </span>
                    </>
                  );
                  const leva = l.topico && l.topico !== topicoAtual && aoIrAoTopico;
                  return (
                    <li key={l.chave}>
                      {leva ? (
                        <button
                          type="button"
                          className="trilha-item"
                          title="Abrir o tópico onde se faz este levantamento"
                          onClick={() => aoIrAoTopico(l.topico!)}
                        >
                          {corpo}
                          <span className="trilha-item-seta">
                            <Icone nome="avancar" tamanho={12} />
                          </span>
                        </button>
                      ) : (
                        <span className="trilha-item">{corpo}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>

      <details className="trilha-legenda">
        <summary>Como ler a trilha</summary>
        <ul>
          {LEGENDA.map((x) => (
            <li key={x.sinal}>
              <Marca sinal={x.sinal} />
              <span>
                <strong>{x.palavra}</strong> — {x.quer}
              </span>
            </li>
          ))}
        </ul>
        <p className="ajuda">
          A conta de cada etapa é quantos levantamentos já têm resposta, entre os que o nível
          contrata e o Hub alcança. A trilha só lê o que está registrado: não faz consulta e não
          trava nada.
        </p>
      </details>
    </div>
  );
}
